/**
 * Firestore error codes worth retrying instead of failing immediately.
 * `failed-precondition` is the common one on mobile: it's what
 * `persistentSingleTabManager` throws when another tab/PWA instance already
 * holds the IndexedDB persistence lease (e.g. a shared link opened in an
 * in-app browser while the installed PWA is still running) — usually
 * resolves itself within a retry or two, not a real "this doesn't exist".
 */
const TRANSIENT_FIRESTORE_CODES = new Set([
  'failed-precondition',
  'unavailable',
  'deadline-exceeded',
  'aborted',
  'cancelled',
]);

export const isTransientFirestoreError = (error) =>
  TRANSIENT_FIRESTORE_CODES.has(error?.code);

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Runs `fn` with exponential-backoff retry on transient Firestore errors.
 * Rethrows immediately on any other error, and rethrows the last error once
 * `attempts` is exhausted — callers should treat a thrown error as "the
 * fetch failed", distinct from a resolved `null`/non-existent result.
 */
export async function withFirestoreRetry(fn, { attempts = 3, baseDelayMs = 300 } = {}) {
  let lastError;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (!isTransientFirestoreError(error) || attempt === attempts - 1) {
        throw error;
      }
      await sleep(baseDelayMs * 2 ** attempt + Math.random() * 100);
    }
  }
  throw lastError;
}

/**
 * Bounds a promise so a hung read (e.g. a stale IndexedDB connection on iOS
 * Safari that never resolves) doesn't leave the caller stuck forever.
 */
export function withTimeout(promise, ms, timeoutError = new Error('firestore_timeout')) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(timeoutError), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}
